/**
 * How to fold the card: every step shown on the person's own card, as the
 * paper really moves (src/template/fold-guide.js via fold-stage.tsx), with
 * the words from src/template/fold-steps.js - the same steps, numbers and
 * wording as the lines printed on the sheet and the email that carries it.
 *
 * One step at a time up top: it plays by itself when opened (unless the
 * browser asks for reduced motion), and can be paused, replayed or scrubbed
 * through slowly. Underneath, every step at once as a before / after pair -
 * the part that prints, as a sheet of instructions to fold from.
 *
 * Keyboard: the left and right arrows step back and forward.
 *
 * 2026-10-17: the player keeps one size whatever the step - the picture and the words each
 * in a box of fixed height, the buttons at the same place - so nothing moves up and down
 * from step to step; the way back to the card is a button.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { withShapes, type FoldGuide, type PaperStyle, type PrintGuides, type ProjectDocument, type TemplateDocument, type ZoneShapes, type ZoneSymbol, type ZoneText } from '@foldmycard/scene';
import { api, type ImageItem, type ProjectItem } from '../api.ts';
import { JourneyView, SheetFace, shownImage, type ShownImage } from '../card-view.tsx';
import { FoldStage, stepSeconds, type FoldStep } from '../fold-stage.tsx';
import { symbolsOf } from '../symbol.tsx';
import { PreviewDialog } from '../preview.tsx';
import { Link, useLocation } from '../router.tsx';
import { envelopeFits } from '../envelope/designs.ts';
import { t } from '../strings.ts';
import { SignOutButton } from '../idle.tsx';
import { TipButton } from '../tip.tsx';

const reducedMotion = () => {
  try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

interface Card {
  template: TemplateDocument;
  pictures: Map<number, ShownImage>;
  notes: Map<number, ZoneText>;
  symbols: Map<number, ZoneSymbol>;
  paper?: PaperStyle;
  mono?: boolean;
  plain?: Set<number>;
}

/** The step's animation: plays once when the step opens, then waits at the end. */
function useStepPlayer(step: FoldStep | null) {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const live = useRef({ progress, step });
  live.current = { progress, step };

  useEffect(() => {
    setProgress(0);
    setPlaying(false);
    if (!step || reducedMotion()) return;
    const start = setTimeout(() => setPlaying(true), 1100);   // a moment to see what is about to fold
    return () => clearTimeout(start);
  }, [step]);

  useEffect(() => {
    if (!playing || !step) return;
    let raf = 0;
    let last = performance.now();
    const seconds = stepSeconds(step);
    const tick = (now: number) => {
      const next = Math.min(1, live.current.progress + (now - last) / 1000 / seconds);
      last = now;
      setProgress(next);
      if (next < 1) raf = requestAnimationFrame(tick);
      else setPlaying(false);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, step]);

  return {
    progress, playing,
    toggle: () => {
      if (playing) return setPlaying(false);
      if (live.current.progress >= 1) setProgress(0);
      setPlaying(true);
    },
    scrub: (p: number) => { setPlaying(false); setProgress(p); },
  };
}

function SideLegend() {
  return (
    <p className="fold-legend small muted">
      <span className="swatch-inline side-1" aria-hidden="true" /> {t('foldSide1')}
      <span className="swatch-inline side-2" aria-hidden="true" /> {t('foldSide2')}
      <span className="fold-legend-note">{t('foldSideLegend')}</span>
    </p>
  );
}

function StepPlayer({ card, step }: { card: Card; step: FoldStep }) {
  const player = useStepPlayer(step);
  return (
    <figure className="fold-figure">
      <div className="fold-canvas">
        <FoldStage {...card} step={step} progress={player.progress} />
      </div>
      {/* a step with nothing moving (the twisted purse's creasing) is shown still: nothing to play */}
      {step.phases.length > 0 && <figcaption className="fold-controls no-print">
        <button className="primary" onClick={player.toggle}>
          {player.playing ? t('foldPause') : player.progress >= 1 ? t('foldReplay') : t('foldPlay')}
        </button>
        <input type="range" min={0} max={1000} value={Math.round(player.progress * 1000)} aria-label={t('foldProgress')}
          onChange={(e) => player.scrub(Number(e.target.value) / 1000)} />
      </figcaption>}
      <SideLegend />
    </figure>
  );
}

function BeforeYouStart({ card, guides }: { card: Card; guides: PrintGuides | null }) {
  return (
    <figure className="fold-figure">
      <div className="sheet-pair">
        {([1, 2] as const).map((f) => (
          <div key={f}>
            <h3 className="small">{t('sheetFace', { n: f })}</h3>
            <div className="fold-sheet">
              <SheetFace {...card} selected={null} mode="clean" face={f} guides={guides?.faces.find((x) => x.face === f) ?? null} />
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}

function Finished({ card }: { card: Card }) {
  const views = card.template.views;
  return (
    <figure className="fold-figure">
      <div className="sheet-pair">
        {views.filter((v) => v.id === 'f1' || v.id === 'f2').map((v) => (
          <div key={v.id}>
            <h3 className="small">{v.id === 'f1' ? t('foldClosedFront') : t('foldClosedBack')}</h3>
            <JourneyView {...card} selected={null} mode="clean" view={v} />
          </div>
        ))}
      </div>
    </figure>
  );
}

export function FoldPage({ projectId }: { projectId: string }) {
  const q = useLocation().searchParams;
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [baseTemplate, setTemplate] = useState<TemplateDocument | null>(null);
  const [doc, setDoc] = useState<ProjectDocument | null>(null);
  /** The shapes zones 10-13 can take, and the template with this card's choices in place (ProjectDocument.shapes). */
  const [shapeCat, setShapeCat] = useState<ZoneShapes | null>(null);
  const template = useMemo(() => baseTemplate && withShapes(baseTemplate, shapeCat, doc?.shapes, doc?.slide),
    [baseTemplate, shapeCat, doc?.shapes, doc?.slide]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [guides, setGuides] = useState<PrintGuides | null>(null);
  const [folding, setFolding] = useState<FoldGuide | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 0 = before you start, 1..7 = the steps, 8 = finished
  const [at, setAt] = useState(0);
  const [opening, setOpening] = useState(false);
  const top = useRef<HTMLElement>(null);

  useEffect(() => {
    (async () => {
      const { project, versions } = await api.project(projectId);
      const vid = q.get('v') || [...versions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0].id;
      const { version } = await api.version(projectId, vid);
      const [tpl, imgs, g, f, cat] = await Promise.all([
        api.template(project.templateId, project.paper, version.templateHash), api.images(),
        api.guides(project.templateId, project.paper, version.templateHash),
        api.folding(project.templateId, project.paper, version.templateHash),
        api.shapes(project.templateId, project.paper, version.templateHash)]);
      setProject(project);
      setTemplate(tpl);
      setShapeCat(cat);
      setDoc(version.document!);
      setImages(imgs.images);
      setGuides(g);
      setFolding(f);
    })().catch((err) => setError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const byId = useMemo(() => new Map(images.map((i) => [i.id, i])), [images]);
  const pictures = useMemo(() => {
    const m = new Map<number, ShownImage>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) {
      const img = slot.image && !slot.plain && byId.get(slot.image.id);
      if (img) m.set(Number(k), shownImage(img, slot));
    }
    return m;
  }, [doc, byId]);
  const notes = useMemo(() => {
    const m = new Map<number, ZoneText>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) if (slot.text && !slot.plain) m.set(Number(k), slot.text);
    return m;
  }, [doc]);
  /** The zones left plain (ZoneSlot.plain): neither picture nor note shows in them. */
  const plain = useMemo(() => new Set(Object.entries(doc?.zones || {}).filter(([, s]) => s.plain).map(([k]) => Number(k))), [doc]);

  const last = (folding?.steps.length ?? 0) + 1;
  const go = (i: number, scroll = false) => {
    setAt(Math.max(0, Math.min(last, i)));
    if (scroll) top.current?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (opening || (e.target as HTMLElement).closest?.('input, textarea, select')) return;
      if (e.key === 'ArrowRight') setAt((x) => Math.min(last, x + 1));
      if (e.key === 'ArrowLeft') setAt((x) => Math.max(0, x - 1));
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [last, opening]);

  if (error) return <main className="page"><p className="error">{error}</p></main>;
  if (!template || !doc || !project) return <main className="page"><p className="muted">{t('loading')}</p></main>;
  if (!folding) {
    return (
      <main className="page">
        <p className="row spread"><Link href={`/projects/${project.id}`} className="button back">← {t('backToCard')}</Link><span className="row"><TipButton /><SignOutButton /></span></p>
        <p className="error">{t('foldNoGuide')}</p>
      </main>
    );
  }

  // after the early returns above: a plain call, not a hook
  const card: Card = { template, pictures, notes, symbols: symbolsOf(doc), paper: doc?.paper, mono: doc?.mono, plain };
  /** The views past the closed card's front and back: what opening it shows. */
  const opens = template.views.filter((v) => !['f1', 'f2'].includes(v.id));
  const steps = folding.steps;
  const step = at >= 1 && at <= steps.length ? steps[at - 1] : null;
  const names = [t('foldBeforeShort'), ...steps.map((s) => String(s.n)), t('foldDoneShort')];
  // a design's own words, where its folding guide brings them (the letterfold does)
  const intro = folding?.intro;

  return (
    <main className="page fold-page">
      <p className="no-print row spread"><Link href={`/projects/${project.id}${q.get('v') ? `?v=${q.get('v')}` : ''}`} className="button back">← {t('backToCard')}: {project.title}</Link><span className="row"><TipButton /><SignOutButton /></span></p>
      <h1>{t('foldTitle')}</h1>
      <p className="muted fold-intro">{intro?.summary ?? t('foldIntro')}</p>

      <section className="fold-player no-print" ref={top} aria-label={t('foldTitle')}>
        <nav className="fold-nav" aria-label="Steps">
          {names.map((name, i) => (
            <button key={i} aria-current={i === at ? 'step' : undefined} className={i === at ? 'on' : i < at ? 'done' : ''}
              onClick={() => go(i)}>{name}</button>
          ))}
        </nav>
        <div className="fold-main">
          {step ? <StepPlayer key={step.n} card={card} step={step} />
            : at === 0 ? <BeforeYouStart card={card} guides={guides} /> : <Finished card={card} />}
          <div className="fold-text" aria-live="polite">
            <div className="fold-text-body">
            {step ? (
              <>
                <p className="fold-eyebrow">{t('foldStepOf', { n: step.n, total: steps.length })}</p>
                <h2>{step.title}</h2>
                <p>{step.text}</p>
                <p className="fold-tip"><strong>{t('foldTip')}</strong> {step.tip}</p>
                <p className="fold-check"><strong>{t('foldCheck')}</strong> {step.check}</p>
              </>
            ) : at === 0 ? (
              <>
                <h2>{t('foldBefore')}</h2>
                <ul className="fold-list">
                  <li>{t('foldNeed')}</li>
                  <li>{t('foldCheckPrint')}</li>
                  <li>{intro?.towards ?? t('foldTowards')}</li>
                  <li>{intro?.lines ?? t('foldLines')}</li>
                  <li><strong>{intro?.start ?? t('foldStart')}</strong></li>
                </ul>
              </>
            ) : (
              <>
                <h2>{t('foldDone')}</h2>
                <p>{intro?.done ?? t('foldDoneText')}</p>
                {/* a card that opens: what the recipient finds, one step at a time (the photo frame does not open) */}
                {opens.length > 0 && (
                  <>
                    <h3>{t('foldOpens')}</h3>
                    <p className="muted">{t('foldOpensText')}</p>
                    <ol className="fold-list">
                      {opens.map((v) => <li key={v.id}>{v.title}</li>)}
                    </ol>
                  </>
                )}
                <span className="row">
                  <button className="primary" onClick={() => setOpening(true)}>{t(opens.length ? 'foldSeeOpen' : 'preview')}</button>
                  {envelopeFits(template) && <Link href={`/projects/${project.id}/envelope${q.get('v') ? `?v=${q.get('v')}` : ''}`} className="button">{t('envMake')} ›</Link>}
                </span>
              </>
            )}
            </div>
            <div className="row fold-step-buttons">
              <button onClick={() => go(at - 1)} disabled={at === 0}>‹ {t('previous')}</button>
              <button className={at < last ? 'primary' : ''} onClick={() => go(at + 1)} disabled={at === last}>{t('next')} ›</button>
            </div>
          </div>
        </div>
      </section>

      <section className="fold-all">
        <div className="row spread">
          <h2>{t('foldAll')}</h2>
          <button className="no-print" onClick={() => window.print()}>{t('foldPrint')}</button>
        </div>
        <ol className="fold-grid">
          {steps.map((st) => (
            <li key={st.n} className="fold-card">
              <h3><span className="fold-num">{st.n}</span> {st.title}</h3>
              <div className="fold-pair">
                <figure>
                  <FoldStage {...card} step={st} progress={0} fit="tight" label={`${t('foldHere')}: ${st.title}`} />
                  <figcaption className="small muted">{t('foldHere')}</figcaption>
                </figure>
                <figure>
                  <FoldStage {...card} step={st} progress={1} marks={false} fit="tight" label={`${t('foldResult')}: ${st.title}`} />
                  <figcaption className="small muted">{t('foldResult')}</figcaption>
                </figure>
              </div>
              <p className="small">{st.text}</p>
              <button className="small no-print" onClick={() => go(st.n, true)}>{t('foldPlay')} ›</button>
            </li>
          ))}
        </ol>
      </section>

      {opening && (
        <PreviewDialog {...card} startAt="f3" onClose={() => setOpening(false)} />
      )}
    </main>
  );
}
